import React, {
  useState,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
  useMemo,
  useLayoutEffect,
} from "react";

import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { toast } from "react-toastify";

import DiscardChangesConfirmModal from "../DiscardChangesConfirmModal";
import EditSpec from "../EditSpec";
import {
  parseSpecDetail,
  isCustomSpecProduct,
  formatSpec,
  handleCheckUnit,
  checkIfSpecsInvalid,
  snapshotGlobalspecCustomProductForRestore,
  applyGlobalspecCustomSnapshotToProduct,
  buildGlobalSpecsUpdateRequestBody,
  globalTestSpecMrlMissingSelectedMarkets,
  globalTestSpecEditorHasMrlCountryListData,
  globalTestSpecEditorRowsMatchForDraft,
} from "../Helper";
import ProductListView from "../ProductListView";
import ProductSpecificSpecsModal from "../ProductSpecificSpecsModal";
import UpdateProductList from "../UpdateProductList";
import GlobalTestSpecList from "./GlobalTestSpecList";
import SaveSuccessModal from "./SaveSuccessModal";
import TestSpecOverview from "./TestSpecOverview";

import { hasPermission, permissions } from "../../../../../../utils/Permissions";

import { getGlobalSpecs, postGlobalSpecs } from "../../../../../../actions/specManager";

import { ReactComponent as LoadingDots } from "../../../../../../assets/images/common/LoadingDots.svg";

import "./GlobalTestSpec.css";

/** Mode enum for Global Test Spec: list | view | add | edit */
const GLOBAL_SPEC_MODE = {
  LIST: "list",
  VIEW: "view",
  ADD: "add",
  EDIT: "edit",
};

/** Stable multiset of selected product ids (directory rows differ from API shape; deep isEqual stays false after add/remove). */
const sortedGlobalSpecProductIds = (products) => [
  ...(products ?? []).map((p) => p?.product_id).filter((id) => id != null && id !== ""),
]
  .map((id) => String(id))
  .sort();

/**
 * Self-contained container for the global test spec feature.
 * Completely separate from product spec - list view, edit flow, and state.
 */
const GlobalTestSpec = forwardRef(({
  scrollbarRef,
  onCloseDrawer,
  onDataRefresh,
  onEditModeChange,
  toggleSpecsUpdated,
  fields = [],
  linkReportJsonFields = [],
}, ref) => {
  const [mode, setMode] = useState(GLOBAL_SPEC_MODE.LIST);
  const [selectedTestName, setSelectedTestName] = useState("");
  const [dataOfSpecEditing, setDataOfSpecEditing] = useState([]);
  const [loadingEditSpecData, setLoadingEditSpecData] = useState(false);
  const [specDataChanged, setSpecDataChanged] = useState(false);
  const [confirmExitModalDisplay, setConfirmExitModalDisplay] = useState(false);
  const [valueHasBeenEdited, setValueHasBeenEdited] = useState(false);
  const [productsForTest, setProductsForTest] = useState([]);
  /** True when getGlobalSpecs failed; overview stays visible but edit must not advance the workflow. */
  const [testSpecDetailLoadFailed, setTestSpecDetailLoadFailed] = useState(false);
  const [testSpecStatus, setTestSpecStatus] = useState("added"); // "added" | "pending"
  const [testSpecProductSearchValue, setTestSpecProductSearchValue] = useState("");
  const [testSpecStep1Baseline, setTestSpecStep1Baseline] = useState(null);
  const exitingRef = useRef(false);

  const [productSpecificSpecsModalOpen, setProductSpecificSpecsModalOpen] = useState(false);
  const [isProductSpecificSpecsSaving, setIsProductSpecificSpecsSaving] = useState(false);
  const [saveSuccessModal, setSaveSuccessModal] = useState({
    open: false,
    testDisplayName: "",
    saveAction: "update",
    updateOperation: "standard_only",
    affectedProductCount: 0,
    hadCustomInSelection: false,
  });
  /** standardOnly | customOnly | mixed */
  const [productSpecificModalVariant, setProductSpecificModalVariant] = useState("mixed");

  /** Per-session: globalspec custom rows keyed by product_id; survives remove/re-add from directory. */
  const globalspecCustomSnapshotsRef = useRef(new Map());

  /** Align with product Spec Manager: only admin/editor (add_spec) may change global test specs. */
  const canEditTestSpecs = hasPermission(permissions.specManager.addSpec);

  const rebuildGlobalspecCustomSnapshots = useCallback((products) => {
    const next = new Map();
    (products ?? []).forEach((p) => {
      if (!p?.product_id) return;
      const snap = snapshotGlobalspecCustomProductForRestore(p);
      if (snap) next.set(p.product_id, snap);
    });
    globalspecCustomSnapshotsRef.current = next;
  }, []);

  /**
   * Step-1 baseline drives draft detection and preview gating. Uses API-local snapshots in async open
   * (state may not have flushed yet); otherwise current React state.
   */
  const seedTestSpecStep1Baseline = useCallback((productsSnapshot, specSnapshot) => {
    setTestSpecStep1Baseline({
      products: cloneDeep(productsSnapshot ?? productsForTest),
      spec: cloneDeep(specSnapshot ?? dataOfSpecEditing),
    });
    setSpecDataChanged(false);
  }, [productsForTest, dataOfSpecEditing]);

  const openTestSpecEditor = async (testName, status = "added") => {
    const emptySpec = {
      test: testName, test_type: "", specs: ["", "", ""], removable: true,
    };
    setTestSpecStep1Baseline(null);
    setSelectedTestName(testName);
    setTestSpecStatus(status === "pending" ? "pending" : "added");
    setMode(GLOBAL_SPEC_MODE.VIEW);
    if (onEditModeChange) onEditModeChange(true);
    setTestSpecDetailLoadFailed(false);
    setLoadingEditSpecData(true);
    setDataOfSpecEditing([]);
    setProductsForTest([]);
    rebuildGlobalspecCustomSnapshots([]);
    try {
      const response = await getGlobalSpecs({ test_display_name: testName });
      if (!response.success) {
        toast.error(response.message);
        setTestSpecDetailLoadFailed(true);
        setLoadingEditSpecData(false);
        return;
      }
      const products = response.result.products ?? [];
      setProductsForTest(products);
      rebuildGlobalspecCustomSnapshots(products);

      if (response.result) {
        const {
          test, min, max, unit, countries_list,
        } = response.result;
        const countriesLst = countries_list ?? [];
        const spec = {
          test,
          min: min ?? "",
          max: max ?? "",
          unit: unit ?? "",
          countries_lst: countriesLst,
        };
        const hasSpecValues = (min || max || unit);
        const hasMRLCountries = Array.isArray(countriesLst) && countriesLst.length > 0;
        const specToUse = hasSpecValues || hasMRLCountries
          ? [parseSpecDetail(spec)]
          : [emptySpec];
        setDataOfSpecEditing(specToUse);
        if (status === "pending" && canEditTestSpecs) {
          seedTestSpecStep1Baseline(products, specToUse);
          setMode(GLOBAL_SPEC_MODE.ADD);
        }
      }
      setLoadingEditSpecData(false);
    } catch {
      toast.error("Failed to fetch test spec data.");
      setTestSpecDetailLoadFailed(true);
      setDataOfSpecEditing([emptySpec]);
      rebuildGlobalspecCustomSnapshots([]);
      setLoadingEditSpecData(false);
    }
  };

  const openEditMode = () => {
    if (!canEditTestSpecs) return;
    // Switch from VIEW -> Add/Edit UI.
    // Re-assert edit mode to keep SpecManager layout in sync.
    if (onEditModeChange) onEditModeChange(true);
    seedTestSpecStep1Baseline();
    setMode(testSpecStatus === "pending" ? GLOBAL_SPEC_MODE.ADD : GLOBAL_SPEC_MODE.EDIT);
  };

  /** Product list vs multiset of baseline ids; spec side uses `specDataChanged` synced in {@link handleDataChange} vs step-1 baseline spec. */
  const testSpecEditHasDraftChanges = useMemo(() => {
    if (!testSpecStep1Baseline) return false;
    const baselineIds = sortedGlobalSpecProductIds(testSpecStep1Baseline.products);
    const currentIds = sortedGlobalSpecProductIds(productsForTest);
    const productsSelectionChanged = !isEqual(baselineIds, currentIds);
    return productsSelectionChanged || specDataChanged;
  }, [productsForTest, specDataChanged, testSpecStep1Baseline]);

  const handleDataChange = (specList) => {
    // TestSpecForm mutates the spec array in place before calling this handler.
    // cloneDeep ensures React state and baseline diffing (isEqual) see real edits.
    const next = specList && specList.length ? cloneDeep(specList) : [];
    setDataOfSpecEditing(next);
    if (testSpecStep1Baseline) {
      const specMatchesBaseline = globalTestSpecEditorRowsMatchForDraft(
        next,
        testSpecStep1Baseline.spec,
      );
      setSpecDataChanged(!specMatchesBaseline);
    }
  };

  const buildGlobalspecsPostPayload = useCallback((updateOperation) => {
    const specList = handleCheckUnit(formatSpec(dataOfSpecEditing));
    const firstSpec = specList[0] ?? {};
    const includeMrlCountriesList = globalTestSpecEditorHasMrlCountryListData(dataOfSpecEditing);

    return buildGlobalSpecsUpdateRequestBody({
      testDisplayName: selectedTestName,
      formattedFirstSpec: firstSpec,
      updateOperation,
      products: productsForTest,
      includeMrlCountriesList,
    });
  }, [dataOfSpecEditing, productsForTest, selectedTestName]);

  const closeModalAfterSuccess = () => {
    setProductSpecificSpecsModalOpen(false);
    setProductSpecificModalVariant("mixed");
  };

  const finalizeSave = async (payload, saveMeta) => {
    try {
      const result = await postGlobalSpecs(payload);
      if (!result.success) {
        toast.error(result.message);
        return false;
      }
      setValueHasBeenEdited(true);
      if (toggleSpecsUpdated) toggleSpecsUpdated(true);
      setSaveSuccessModal({
        open: true,
        testDisplayName: selectedTestName,
        saveAction: saveMeta.saveAction,
        updateOperation: saveMeta.updateOperation,
        affectedProductCount: saveMeta.affectedProductCount,
        hadCustomInSelection: saveMeta.hadCustomInSelection,
      });
      closeModalAfterSuccess();
      return true;
    } catch {
      toast.error("Failed to save spec.");
      return false;
    }
  };

  const handleSaveSpec = async () => {
    if (!canEditTestSpecs) {
      toast.error("You do not have permission to edit test specs.");
      return false;
    }
    const isMRL = globalTestSpecEditorHasMrlCountryListData(dataOfSpecEditing);

    if (isMRL && globalTestSpecMrlMissingSelectedMarkets(dataOfSpecEditing)) {
      toast.error("Please select at least one country for MRL tests.");
      return false;
    }
    if (checkIfSpecsInvalid(dataOfSpecEditing)) {
      toast.error("Invalid or missing value(s).");
      return false;
    }

    const customProductsCount = productsForTest.filter(isCustomSpecProduct).length;
    const standardProductsCount = productsForTest.length - customProductsCount;

    let modalVariant = "mixed";
    if (customProductsCount === 0) modalVariant = "standardOnly";
    else if (standardProductsCount === 0) modalVariant = "customOnly";

    setProductSpecificModalVariant(modalVariant);
    setProductSpecificSpecsModalOpen(true);
    return false;
  };

  const handleModalCancel = () => {
    setProductSpecificSpecsModalOpen(false);
    setProductSpecificModalVariant("mixed");
  };

  const handleSaveSuccessModalClose = () => {
    setSaveSuccessModal({
      open: false,
      testDisplayName: "",
      saveAction: "update",
      updateOperation: "standard_only",
      affectedProductCount: 0,
      hadCustomInSelection: false,
    });
    setMode(GLOBAL_SPEC_MODE.LIST);
    if (onEditModeChange) onEditModeChange(false);
    setSelectedTestName("");
    setDataOfSpecEditing([]);
    setProductsForTest([]);
    rebuildGlobalspecCustomSnapshots([]);
    setSpecDataChanged(false);
    setTestSpecStep1Baseline(null);
  };

  /** Mixed selection + user chose “standard spec products only” radio → `standard_only`. */
  const handleModalApplyStandardOnly = async () => {
    if (isProductSpecificSpecsSaving) return;
    setIsProductSpecificSpecsSaving(true);
    const customN = productsForTest.filter(isCustomSpecProduct).length;
    const standardN = productsForTest.length - customN;
    const payload = buildGlobalspecsPostPayload("standard_only");
    await finalizeSave(payload, {
      saveAction: mode === GLOBAL_SPEC_MODE.ADD ? "add" : "update",
      updateOperation: "standard_only",
      affectedProductCount: standardN,
      hadCustomInSelection: customN > 0,
    });
    setIsProductSpecificSpecsSaving(false);
  };

  /** All-custom modal, or mixed + “all selected” radio → `all_selected`. */
  const handleModalApplyAllSelected = async () => {
    if (isProductSpecificSpecsSaving) return;
    setIsProductSpecificSpecsSaving(true);
    const customN = productsForTest.filter(isCustomSpecProduct).length;
    const payload = buildGlobalspecsPostPayload("all_selected");
    await finalizeSave(payload, {
      saveAction: mode === GLOBAL_SPEC_MODE.ADD ? "add" : "update",
      updateOperation: "all_selected",
      affectedProductCount: productsForTest.length,
      hadCustomInSelection: customN > 0,
    });
    setIsProductSpecificSpecsSaving(false);
  };

  /** Only standard products in selection — send `standard_only` (full `product_list` preserved). */
  const handleModalSaveAllStandardProducts = async () => {
    if (isProductSpecificSpecsSaving) return;
    setIsProductSpecificSpecsSaving(true);
    const payload = buildGlobalspecsPostPayload("standard_only");
    await finalizeSave(payload, {
      saveAction: mode === GLOBAL_SPEC_MODE.ADD ? "add" : "update",
      updateOperation: "standard_only",
      affectedProductCount: productsForTest.length,
      hadCustomInSelection: false,
    });
    setIsProductSpecificSpecsSaving(false);
  };

  const handleExitEditSpec = (
    exitDrawer = false,
    refreshList = false,
  ) => {
    setTestSpecStep1Baseline(null);
    if (exitDrawer) {
      if (onEditModeChange) onEditModeChange(false);
      onCloseDrawer();
      setSpecDataChanged(false);
      rebuildGlobalspecCustomSnapshots([]);
    } else {
      if (refreshList && onDataRefresh) onDataRefresh();
      setMode(GLOBAL_SPEC_MODE.LIST);
      setSelectedTestName("");
      setDataOfSpecEditing([]);
      setProductsForTest([]);
      rebuildGlobalspecCustomSnapshots([]);
      setConfirmExitModalDisplay(false);
      setSpecDataChanged(false);
      if (onEditModeChange) onEditModeChange(false);
    }
  };

  const confirmExitModalDisplayToggle = () => {
    setConfirmExitModalDisplay((prev) => !prev);
    exitingRef.current = false;
    return true;
  };

  const requestClose = useCallback(() => {
    if (specDataChanged) {
      setConfirmExitModalDisplay(true);
      exitingRef.current = true;
    } else {
      if (onEditModeChange) onEditModeChange(false);
      onCloseDrawer();
    }
    if (valueHasBeenEdited && onDataRefresh) {
      onDataRefresh();
      setValueHasBeenEdited(false);
    }
  }, [specDataChanged, valueHasBeenEdited, onCloseDrawer, onDataRefresh, onEditModeChange]);

  const handleSelectedProductsChange = useCallback((products) => {
    const list = products ?? [];
    const merged = list.map((p) => {
      const snap = globalspecCustomSnapshotsRef.current.get(p.product_id);
      return applyGlobalspecCustomSnapshotToProduct(p, snap);
    });
    setProductsForTest(merged);
  }, []);

  const renderProductSelectionSection = useCallback(
    ({ onFooterStateChange, onRegisterAddProductsAction }) => (
      <UpdateProductList
        layout="stacked"
        selectedProducts={productsForTest}
        onSelectedProductsChange={handleSelectedProductsChange}
        fields={fields}
        linkReportJsonFields={linkReportJsonFields}
        searchValue={testSpecProductSearchValue}
        onSearchValueChange={setTestSpecProductSearchValue}
        onFooterStateChange={onFooterStateChange}
        onRegisterAddProductsAction={onRegisterAddProductsAction}
      />
    ),
    [
      productsForTest,
      handleSelectedProductsChange,
      fields,
      linkReportJsonFields,
      testSpecProductSearchValue,
    ],
  );

  const productListViewSection = (
    <ProductListView
      products={productsForTest}
      fields={fields}
      linkReportJsonFields={linkReportJsonFields}
    />
  );

  useImperativeHandle(ref, () => ({
    requestClose,
  }), [requestClose]);

  const isViewMode = mode === GLOBAL_SPEC_MODE.VIEW;
  const isEditView = mode === GLOBAL_SPEC_MODE.ADD || mode === GLOBAL_SPEC_MODE.EDIT;

  useLayoutEffect(() => {
    if (!isEditView || loadingEditSpecData || testSpecStep1Baseline != null) return;
    if (!canEditTestSpecs) return;
    seedTestSpecStep1Baseline();
  }, [
    isEditView,
    loadingEditSpecData,
    testSpecStep1Baseline,
    canEditTestSpecs,
    seedTestSpecStep1Baseline,
  ]);

  if (isViewMode) {
    if (loadingEditSpecData) {
      return (
        <div className="GlobalTestSpec__EditWrapper">
          <div className="GlobalTestSpec__ViewLoading">
            <LoadingDots width={28} height={28} fill="#19305A" />
          </div>
        </div>
      );
    }

    return (
      <div className="GlobalTestSpec__EditWrapper">
        <TestSpecOverview
          selectedTestName={selectedTestName}
          testStandardSpec={dataOfSpecEditing[0] ?? {
            test: selectedTestName,
            test_type: "",
            specs: ["", "", ""],
            removable: true,
          }}
          products={productsForTest}
          fields={fields}
          linkReportJsonFields={linkReportJsonFields}
          testSpecStatus={testSpecStatus}
          canEditTestSpec={canEditTestSpecs && !testSpecDetailLoadFailed}
          onBack={() => handleExitEditSpec(false, false)}
          onEdit={openEditMode}
        />
      </div>
    );
  }

  if (isEditView) {
    const isAddMode = mode === GLOBAL_SPEC_MODE.ADD;
    const modalCustomProductsCount = productsForTest.filter(isCustomSpecProduct).length;
    const modalStandardProductsCount = productsForTest.length - modalCustomProductsCount;
    return (
      <div className="GlobalTestSpec__EditWrapper">
        <EditSpec
          fromModal={false}
          addingSpec={isAddMode}
          // Always start EditSpec in "editing" mode for the Test Spec flow.
          // This prevents showing the legacy (non-edit) Test Spec view screen inside EditSpec.
          editingSpec
          productIdentifierArr={{ "Test name": selectedTestName }}
          dataOfSpecEditing={dataOfSpecEditing}
          disableEditForMRL={false}
          confirmExitModalDisplayToggle={confirmExitModalDisplayToggle}
          handleExitEdit={handleExitEditSpec}
          handleSaveSpec={handleSaveSpec}
          handleDataChange={handleDataChange}
          handleSpecChanged={setSpecDataChanged}
          specChanged={specDataChanged}
          handleClosingEditSpec={() => {}}
          valueHasBeenEdited={valueHasBeenEdited}
          loading={loadingEditSpecData}
          showUpdateTestList={false}
          setShowUpdateTestList={() => {}}
          handleSaveTestList={async () => false}
          isEditingTestSpec
          renderProductSelectionSection={renderProductSelectionSection}
          productListViewSection={productListViewSection}
          testSpecPreviewSelectedProducts={productsForTest}
          testSpecPreviewFields={fields}
          testSpecPreviewLinkReportJsonFields={linkReportJsonFields}
          selectedProductCount={productsForTest.length}
          testSpecSearchValue={testSpecProductSearchValue}
          onTestSpecSearchValueChange={setTestSpecProductSearchValue}
          testSpecEditHasDraftChanges={testSpecEditHasDraftChanges}
          testSpecHasStep1Baseline={testSpecStep1Baseline != null}
        />
        <ProductSpecificSpecsModal
          open={productSpecificSpecsModalOpen}
          variant={productSpecificModalVariant}
          onClose={handleModalCancel}
          customProductsCount={modalCustomProductsCount}
          standardProductsCount={modalStandardProductsCount}
          loading={isProductSpecificSpecsSaving}
          onApplyStandardOnly={handleModalApplyStandardOnly}
          onApplyAllSelected={handleModalApplyAllSelected}
          onSaveAllStandardProducts={handleModalSaveAllStandardProducts}
          testSpecSaveAction={isAddMode ? "add" : "update"}
        />
        <SaveSuccessModal
          open={saveSuccessModal.open}
          onClose={handleSaveSuccessModalClose}
          testDisplayName={saveSuccessModal.testDisplayName}
          saveAction={saveSuccessModal.saveAction}
          updateOperation={saveSuccessModal.updateOperation}
          affectedProductCount={saveSuccessModal.affectedProductCount}
          hadCustomInSelection={saveSuccessModal.hadCustomInSelection}
        />
        {confirmExitModalDisplay && (
          <DiscardChangesConfirmModal
            headerText="Discard unsaved changes?"
            bodyText="Changes made will not be saved if you exit."
            buttonText={["Discard Changes", "Continue Editing"]}
            buttonFunctions={[
              () => handleExitEditSpec(exitingRef.current),
              confirmExitModalDisplayToggle,
            ]}
          />
        )}
      </div>
    );
  }

  return (
    <GlobalTestSpecList
      scrollbarRef={scrollbarRef}
      onEditTest={openTestSpecEditor}
    />
  );
});

GlobalTestSpec.displayName = "GlobalTestSpec";

export default GlobalTestSpec;
